import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import CustomerPaymentApi from './customerPaymentApi';
import { CustomerPayment, ProjectWithInvoices, InvoiceWithPayments } from './customerPaymentType';
import { formatAddress, formatTimestamp, formatDate } from '../lib/global_function';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';

// Map database values to user-friendly display labels
const PAYMENT_METHOD_LABELS: { [key: string]: string } = {
  bank_transfer: 'Bank Transfer',
  credit_card: 'Credit Card',
  cash: 'Cash',
  check: 'Check',
  money_order: 'Money Order',
};

const DeleteCustomerPayment = () => {
  const location = useLocation();
  const { project, invoice, customer_payment, customer } = location.state || {};
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const breadcrumbs = [
    { label: 'Customer Payment', path: '/main/customerpayment', state: { customer } },
    { label: 'Delete Payment', path: '' },
  ];

  const handleDeletePayment = async () => {
    if (!customer_payment?.customer_payment_id) {
      setErrorMessage('Payment ID is missing.');
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      await CustomerPaymentApi.deleteCustomerPaymentByPaymentId(customer_payment.customer_payment_id);
      setSuccessMessage('Delete successful');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An error occurred while deleting the payment.');
    } finally {
      setLoading(false);
    }
  };

  const formatCustomerAddress = () =>
    customer
      ? formatAddress(customer.address_1, customer.address_2, customer.city, customer.state, customer.zip)
      : '';

  return (
    <>
      <HorizontalMenu />
      <div className="container mx-auto max-w-7xl px-4 py-6">
        <Breadcrumbs items={breadcrumbs} />

        <h2 className="text-2xl font-semibold mb-6">Delete Customer Payment</h2>

        {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
        {successMessage ? (
          <Alert variant="success">{successMessage}</Alert>
        ) : (
          <>
            {customer && (
              <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-white">
                <h3 className="text-base font-semibold text-foreground mb-2">
                  Customer: {customer.first_name} {customer.last_name}
                </h3>
                <div className="text-sm">
                  <span className="font-semibold">Address:</span>{' '}
                  <span className="text-paragraph">{formatCustomerAddress()}</span>
                </div>
              </div>
            )}

            {project && invoice && customer_payment ? (
              <div className="space-y-4">
                {/* Project Information */}
                <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                  <span className="font-semibold">Project Name:</span>{' '}
                  <span className="text-paragraph">{project.name || `Project #${project.project_id}`}</span>
                  {', '}
                  <span className="font-semibold">Description:</span>{' '}
                  <span className="text-paragraph">{project.project_description || 'N/A'}</span>
                  {', '}
                  <span className="font-semibold">Type:</span>{' '}
                  <span className="text-paragraph">{project.project_type || 'N/A'}</span>
                </div>

                {/* Invoice Information */}
                <div className="p-4 border border-gray-300 rounded-lg bg-white">
                  <div className="mb-2">
                    <span className="font-semibold">Invoice Name:</span>{' '}
                    <span className="text-paragraph">{invoice.name || 'N/A'}</span>
                    {', '}
                    <span className="font-semibold">Invoice ID:</span>{' '}
                    <span className="text-paragraph">{invoice.invoice_id || 'N/A'}</span>
                    {', '}
                    <span className="font-semibold">Issue Date:</span>{' '}
                    <span className="text-paragraph">{formatDate(invoice.issue_date) || 'N/A'}</span>
                    {', '}
                    <span className="font-semibold">Due Date:</span>{' '}
                    <span className="text-paragraph">{formatDate(invoice.due_date) || 'N/A'}</span>
                    {', '}
                    <span className="font-semibold">Total:</span>{' '}
                    <span className="text-paragraph">${Number(invoice.final_total || 0).toFixed(2)}</span>
                    {', '}
                    <span className="font-semibold">Paid:</span>{' '}
                    <span className="text-green-600 font-medium">
                      ${Number(invoice.amount_paid || 0).toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold">Balance Due:</span>{' '}
                    <span
                      className={`font-medium ${
                        (invoice.final_total - invoice.amount_paid) <= 0
                          ? 'text-green-600'
                          : 'text-red-600'
                      }`}
                    >
                      ${Number((invoice.final_total || 0) - (invoice.amount_paid || 0)).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Payment Details */}
                <div className="p-4 border-2 border-gray-400 rounded-lg bg-gray-50">
                  <h3 className="text-base font-semibold text-foreground mb-4">Payment Details</h3>
                  <div className="space-y-3 max-w-sm">
                    <div>
                      <div className="font-semibold text-sm">Amount:</div>
                      <div className="text-paragraph">${Number(customer_payment.amount).toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="font-semibold text-sm">Payment Date:</div>
                      <div className="text-paragraph">{formatTimestamp(customer_payment.payment_date)}</div>
                    </div>
                    <div>
                      <div className="font-semibold text-sm">Payment Method:</div>
                      <div className="text-paragraph">
                        {PAYMENT_METHOD_LABELS[customer_payment.payment_method] || customer_payment.payment_method}
                      </div>
                    </div>
                    <div>
                      <div className="font-semibold text-sm">Note:</div>
                      <div className="text-paragraph min-h-[80px]">{customer_payment.note || '-'}</div>
                    </div>
                  </div>
                  <Button
                    onClick={handleDeletePayment}
                    disabled={loading}
                    size="medium"
                   
                  >
                    <Trash2 size={16} />
                    {loading ? 'Deleting...' : 'Delete Payment'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-4 border border-gray-300 rounded-lg bg-white text-center">
                <p className="text-gray-600 italic">No payment data available.</p>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default DeleteCustomerPayment;